import {
  annualFeeUsd,
  annualMonthlyEquivalentUsd,
  annualSavingsUsd,
  BUNDLED_TIER_CONFIGS,
} from '@tenfour/domain'
import {
  tierPitch,
  WEB_ASSETS,
  type TierPitch,
  type TierPitchEntry,
} from '@tenfour/contracts'
import type { TierCatalogEntry, TierKey } from './api'

/**
 * The ONE place the /upgrade page and the UpgradeModal get their sales copy.
 * Taglines/bullets derive from the runtime tier catalog via tierPitch
 * (@tenfour/contracts) so the claims can never contradict the configured
 * allowances/features/price (#302). Only the persona image and the pre-load
 * price fallback are static here — the tier's NAME deliberately is not:
 * makes the runtime catalog the only authority on tier identity, so the name
 * comes from `tierLabelFrom` below and the surfaces render tier-neutral copy
 * until it resolves.
 */
export const TIER_STATIC: Record<TierKey, { image: string; fallbackPriceUsd: number }> = {
  GROWTH: { image: WEB_ASSETS.personas.lyra, fallbackPriceUsd: 0 },
  PRO: { image: WEB_ASSETS.personas.memory, fallbackPriceUsd: 99 },
  MAX: { image: WEB_ASSETS.personas.invoice, fallbackPriceUsd: 199 },
}

/** A tier's display name from the runtime catalog, or `undefined` when the
 * catalog hasn't resolved one — the non-hook twin of `useTierLabel`.
 *  Callers must have a tier-neutral form for `undefined`; there is no constant
 *  to fall back to on purpose. */
export function tierLabelFrom(
  catalog: TierCatalogEntry[] | undefined,
  tier: TierKey,
): string | undefined {
  return catalog?.find((t) => t.tier === tier)?.label
}

export const TIER_KEYS: TierKey[] = ['GROWTH', 'PRO', 'MAX']

// Pre-load fallback: the bundled defaults mapped into catalog shape, so the
// pitch renders (with stock claims) while the catalog request is in flight.
// The `label` is deliberately blank — a bundled default can carry a price and a
// feature matrix, but never a tier's NAME. `tierPitch` only reads it for
// the "Everything in X" header, which is correctly omitted when it is empty.
const FALLBACK_CATALOG: TierPitchEntry[] = TIER_KEYS.map((tier) => {
  const cfg = BUNDLED_TIER_CONFIGS[tier]
  return {
    tier,
    label: '',
    monthlyFeeUsd: cfg.monthlyFeeUsd,
    features: cfg.features,
    includedVoiceMinutes: cfg.includedVoiceMinutes,
    includedSmsMessages: cfg.includedSmsMessages,
    llmBudgetUsd: cfg.llmBudgetUsd,
    voiceOverageRatePerHourUsd: cfg.voiceOverageRatePerHourUsd,
    smsOverageRatePer100Usd: cfg.smsOverageRatePer100Usd,
    maxGroupChats: cfg.maxGroupChats,
    monthlyAiMeetings: cfg.monthlyAiMeetings,
  }
})

export function pitchFor(catalog: TierCatalogEntry[] | undefined, tier: TierKey): TierPitch {
  return tierPitch(catalog?.length ? catalog : FALLBACK_CATALOG, tier)
}

export function priceFor(catalog: TierCatalogEntry[] | undefined, tier: TierKey): number {
  return (
    catalog?.find((t) => t.tier === tier)?.monthlyFeeUsd ?? TIER_STATIC[tier].fallbackPriceUsd
  )
}

/** What a tier costs on each cadence. Yearly is quoted two ways because the
 *  cards headline the per-month equivalent and the CTA charges the year. The
 *  catalog is authoritative; the pre-load fallback recomputes from the bundled
 *  discount so an in-flight request never renders a bogus $0 saving. */
export interface TierPricing {
  monthlyUsd: number
  annualUsd: number
  annualMonthlyEquivalentUsd: number
  annualSavingsUsd: number
}

export function pricingFor(
  catalog: TierCatalogEntry[] | undefined,
  tier: TierKey,
): TierPricing {
  const entry = catalog?.find((t) => t.tier === tier)
  const monthlyUsd = entry?.monthlyFeeUsd ?? BUNDLED_TIER_CONFIGS[tier].monthlyFeeUsd
  // A catalog served by an API that predates yearly billing carries no annual
  // fields, and arithmetic on `undefined` renders "$NaN billed yearly" on a
  // money page. Recompute from the monthly price + the bundled discount
  // instead — same formula the server uses.
  if (
    entry?.annualFeeUsd != null &&
    entry.annualMonthlyEquivalentUsd != null &&
    entry.annualSavingsUsd != null
  ) {
    return {
      monthlyUsd,
      annualUsd: entry.annualFeeUsd,
      annualMonthlyEquivalentUsd: entry.annualMonthlyEquivalentUsd,
      annualSavingsUsd: entry.annualSavingsUsd,
    }
  }
  const discount = annualDiscountFor(entry, tier)
  return {
    monthlyUsd,
    annualUsd: annualFeeUsd(monthlyUsd, discount),
    annualMonthlyEquivalentUsd: annualMonthlyEquivalentUsd(monthlyUsd, discount),
    annualSavingsUsd: annualSavingsUsd(monthlyUsd, discount),
  }
}

/** A tier's yearly discount, falling back to the bundled default when the
 *  catalog entry is missing or predates yearly billing. */
function annualDiscountFor(
  entry: TierCatalogEntry | undefined,
  tier: TierKey,
): number {
  const fromCatalog = entry?.annualDiscountPercent
  return typeof fromCatalog === 'number' && Number.isFinite(fromCatalog)
    ? fromCatalog
    : BUNDLED_TIER_CONFIGS[tier].annualDiscountPercent
}

/** The largest yearly discount any paid tier offers — the "Save 10%" chip on the
 *  billing toggle. Reads the catalog so an admin edit moves the chip. */
export function maxAnnualDiscountPercent(
  catalog: TierCatalogEntry[] | undefined,
): number {
  const paidTiers = TIER_KEYS.filter(
    (tier) => pricingFor(catalog, tier).monthlyUsd > 0,
  )
  return Math.max(
    ...paidTiers.map((tier) =>
      annualDiscountFor(
        catalog?.find((t) => t.tier === tier),
        tier,
      ),
    ),
  )
}
